import { t } from "@lingui/macro";
import {
  type CapacityAllocation,
  type CapacityAllocationItem,
  CopyCapacityAllocationItemDocument,
  type CopyCapacityAllocationItemMutation,
  type CopyCapacityAllocationItemMutationVariables,
  DeleteCapacityAllocationDocument,
  type DeleteCapacityAllocationMutation,
  type DeleteCapacityAllocationMutationVariables,
  IsDateBeforeLastPlanningUpdateDocument,
  type IsDateBeforeLastPlanningUpdateMutation,
  type IsDateBeforeLastPlanningUpdateMutationVariables,
  IsUserInTaskDocument,
  type IsUserInTaskQuery,
  type IsUserInTaskQueryVariables,
  MoveCapacityAllocationItemDocument,
  type MoveCapacityAllocationItemInput,
  type MoveCapacityAllocationItemMutation,
  type MoveCapacityAllocationItemMutationVariables,
  PlanningDailyViewPeopleDocument,
  type PlanningDailyViewPeopleQuery,
  type PlanningDailyViewPeopleQueryVariables,
  PlanningDailyViewWhereColumn,
  type PlanningFilterOptionsQuery,
  ReorderCapacityAllocationUserItemsDocument,
  type ReorderCapacityAllocationUserItemsMutation,
  type ReorderCapacityAllocationUserItemsMutationVariables,
  type Scalars,
  SqlOperator,
  type User,
} from "@src/__generated__/graphql";
import {
  CapacityAllocationUserItemDetailDocument,
  type CapacityAllocationUserItemDetailQuery,
  type CapacityAllocationUserItemDetailQueryVariables,
} from "@src/__generated__/urql-graphql";
import {
  hasSameItems,
  PlanningDailyViewItemModel,
  PlanningDailyViewUserByDayModel,
  type PlanningDailyViewUserDayModel,
  type TPlanningDailyViewUserByDay,
} from "@src/components/modules/resource-planning/timeline/models";
import {
  EMPTY_FILTERS_HEIGHT,
  getCommonCapacityAllocationTypeOptions,
  USED_FILTERS_HEIGHT,
} from "@src/constants/planning";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import { client } from "@src/services/client";
import type { AppStore } from "@src/stores/AppStore";
import type { BaseStore } from "@src/stores/BaseStore";
import { PlanningViewTypeEnum } from "@src/stores/models/WorkspaceSettings";
import type {
  AffectAllocationOccurrences,
  DragItem,
  MovedAllocationPayload,
} from "@src/types/planning";
import {
  commonQueryVariables,
  commonSearchParams,
} from "@src/utils/apolloHelpers";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { getTodayStart, toApiDate } from "@src/utils/dates";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import type { CSSUnitValue, Time } from "@src/utils/types";
import {
  addDays,
  eachDayOfInterval,
  endOfWeek,
  isBefore,
  isSameDay,
  startOfWeek,
} from "date-fns";
import { uniqBy } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";

type ConfirmAllocationDurationChangeModalData = {
  previousTime: Time | number;
  newTime: Time | number;
  onConfirm: () => void;
  onCancel: () => void;
};

type ConfirmSeparateAllocationModalData = {
  onConfirm: () => void | Promise<unknown>;
};

const MAX_HOVERED_ITEMS = 30;

export type ResourcePlanningFetchDataArgs = {
  backgroundUpdate: boolean;
  loadForAllUsers: boolean;
};

// Structural snapshot instead of a deep clone: optimistic moves only replace these arrays,
// and restoring them keeps the model instances (and their computeds) intact
type PlanningDataSnapshot = {
  data: PlanningDailyViewUserByDayModel[];
  days: Map<PlanningDailyViewUserByDayModel, PlanningDailyViewUserDayModel[]>;
  dayItems: Map<PlanningDailyViewUserDayModel, PlanningDailyViewItemModel[]>;
};

export class ResourcePlanningViewStore implements BaseStore {
  appStore: AppStore;
  @observable selectedDate = new Date();
  @observable data: PlanningDailyViewUserByDayModel[] = [];
  // Not observable – only read when a drag is reverted
  private dataSnapshot: PlanningDataSnapshot | undefined = undefined;
  @observable searchTerm = "";
  @observable active: DragItem | undefined = undefined;
  // Keyed by column container id so only the column under the dragged item re-renders.
  // The value is the indicator line offset, `null` for a column without items.
  @observable dropIndicators = new Map<string, number | null>();
  @observable lastSyncedAt: string | null | undefined = undefined;
  @observable syncingIds = new Map<PlanningDailyViewItemModel["id"], true>();
  // Items mount their actions overlay on hover, so the overlay's menus and tooltips exist only for
  // the last MAX_HOVERED_ITEMS items the user has pointed at. Keyed by id so only the affected item
  // re-renders. The recency order is kept outside MobX, so hovering an item again re-renders nothing.
  @observable hoveredItemIds = new Map<
    PlanningDailyViewItemModel["id"],
    true
  >();
  private hoveredItemsOrder = new Set<PlanningDailyViewItemModel["id"]>();

  @observable
  deletingAllocationItemId = new Set<CapacityAllocation["id"]>();

  private deleteAllocationMutator = new MutationHelper<
    DeleteCapacityAllocationMutation,
    DeleteCapacityAllocationMutationVariables
  >(DeleteCapacityAllocationDocument);

  confirmDurationChangeModal =
    new DisclosureState<ConfirmAllocationDurationChangeModalData>();

  confirmSeparateAllocationModal =
    new DisclosureState<ConfirmSeparateAllocationModalData>();

  where: Filters<PlanningDailyViewWhereColumn>;

  fetcher = new FetchHelper<
    PlanningDailyViewPeopleQuery,
    PlanningDailyViewPeopleQueryVariables
  >(PlanningDailyViewPeopleDocument);

  userInTaskFetcher = new FetchHelper<
    IsUserInTaskQuery,
    IsUserInTaskQueryVariables
  >(IsUserInTaskDocument);

  moveAllocationMutator = new MutationHelper<
    MoveCapacityAllocationItemMutation,
    MoveCapacityAllocationItemMutationVariables
  >(MoveCapacityAllocationItemDocument);
  copyAllocationMutator = new MutationHelper<
    CopyCapacityAllocationItemMutation,
    CopyCapacityAllocationItemMutationVariables
  >(CopyCapacityAllocationItemDocument);
  reorderAllocationMutator = new MutationHelper<
    ReorderCapacityAllocationUserItemsMutation,
    ReorderCapacityAllocationUserItemsMutationVariables
  >(ReorderCapacityAllocationUserItemsDocument);
  syncCheckMutation = new MutationHelper<
    IsDateBeforeLastPlanningUpdateMutation,
    IsDateBeforeLastPlanningUpdateMutationVariables
  >(IsDateBeforeLastPlanningUpdateDocument);

  @observable _fetchData: (
    loadForAllUsers: boolean,
    silent: boolean,
  ) => Promise<any>;

  constructor(
    appStore: AppStore,
    fetchData: (loadForAllUsers: boolean, silent: boolean) => Promise<any>,
    onFilterChange?: () => void,
  ) {
    makeObservable(this);
    this._fetchData = fetchData;
    this.appStore = appStore;
    this.where = new Filters(
      [
        new Filter({
          column: PlanningDailyViewWhereColumn.TeamId,
          operator: SqlOperator.In,
          title: t`Team`,
          options: [],
          hidden: true,
        }),
        new Filter({
          column: PlanningDailyViewWhereColumn.TimeTrackingWorkTypeId,
          operator: SqlOperator.In,
          title: t`Position`,
          options: [],
        }),
        new Filter({
          column: PlanningDailyViewWhereColumn.AllocableType,
          operator: SqlOperator.In,
          title: t`Type`,
          options: getCommonCapacityAllocationTypeOptions(),
        }),
        new Filter({
          column: PlanningDailyViewWhereColumn.TaskPriorityId,
          operator: SqlOperator.In,
          title: t`Priority`,
          options: [],
          isCollapsed: true,
        }),
        new Filter({
          column: PlanningDailyViewWhereColumn.TaskStatusId,
          operator: SqlOperator.In,
          title: t`Task status`,
          options: [],
          isCollapsed: true,
        }),
        new Filter({
          column: PlanningDailyViewWhereColumn.BrandId,
          operator: SqlOperator.In,
          title: t`Client/Brand`,
          tagTitle: t`Brand`,
          options: [],
          sectioned: true,
          hasSelectAllOption: true,
          isCollapsed: true,
        }),
        new Filter({
          column: PlanningDailyViewWhereColumn.UserId,
          title: t`User`,
          operator: SqlOperator.In,
          options: [],
          isCollapsed: true,
        }),
        new Filter({
          column: PlanningDailyViewWhereColumn.CreatedByUserId,
          operator: SqlOperator.In,
          title: t`Creator`,
          options: [],
          isCollapsed: true,
        }),
      ],
      {
        onChange: () => {
          onFilterChange?.();
          this.fetchDailyView();
        },
        tableKey: "resource-planning",
      },
    );
  }

  @computed get searchParams() {
    return commonSearchParams(this);
  }
  @computed get queryParams() {
    return commonQueryVariables(this);
  }

  @computed get selectedDateRange(): [Date, Date] {
    if (
      this.appStore.workspaceStore.settings?.default_planning_view_settings
        .view_type === PlanningViewTypeEnum.WEEK_FROM_NOW
    ) {
      return [this.selectedDate, addDays(this.selectedDate, 6)];
    } else {
      const opts = {
        weekStartsOn: this.appStore.workspaceStore.settings?.startOfWeekNumber,
      };
      return [
        startOfWeek(this.selectedDate, opts),
        endOfWeek(this.selectedDate, opts),
      ];
    }
  }

  @computed get daysOfSelectedInterval(): Date[] {
    const [start, end] = this.selectedDateRange;
    return eachDayOfInterval({
      start,
      end,
    });
  }

  @computed get filterRowHeight(): CSSUnitValue {
    const filtersUsed = this.where.filters.some(({ column, value, hidden }) => {
      if (column === PlanningDailyViewWhereColumn.UserId && hidden)
        return false;
      return value.length;
    });

    return filtersUsed ? USED_FILTERS_HEIGHT : EMPTY_FILTERS_HEIGHT;
  }

  @action setFilterOptions(data: PlanningFilterOptionsQuery) {
    this.where.filtersByColumn
      .get(PlanningDailyViewWhereColumn.TaskStatusId)
      ?.setOptions(
        data.taskStatuses.map(({ id, name }) => ({
          value: id,
          label: name,
        })),
      );

    this.where.filtersByColumn
      .get(PlanningDailyViewWhereColumn.TaskPriorityId)
      ?.setOptions(
        data.taskPriorities.map(({ id, name }) => ({
          value: id,
          label: name,
        })),
      );

    this.where.filtersByColumn
      .get(PlanningDailyViewWhereColumn.BrandId)
      ?.setOptions(
        data.clientsSimpleMap.map((i) => ({
          value: i?.id,
          label: i?.name,
          options: i?.brands.map(({ name, id }) => {
            return {
              label: name,
              value: id,
            };
          })!,
        })),
      );

    this.where.filtersByColumn
      .get(PlanningDailyViewWhereColumn.UserId)
      ?.setOptions(
        data.userSimpleMap.map(({ id, full_name }) => ({
          value: id,
          label: full_name,
        })),
      );

    this.where.filtersByColumn
      .get(PlanningDailyViewWhereColumn.CreatedByUserId)
      ?.setOptions(
        data.userSimpleMap.map(({ id, full_name }) => ({
          value: id,
          label: full_name,
        })),
      );

    this.where.filtersByColumn
      .get(PlanningDailyViewWhereColumn.TimeTrackingWorkTypeId)
      ?.setOptions(
        data.timeTrackingWorkTypes.map(({ id, title }) => ({
          value: id,
          label: title,
        })),
      );

    this.where.filtersByColumn
      .get(PlanningDailyViewWhereColumn.TeamId)
      ?.setOptions(
        data.teamSimpleMap.map(({ id, name }) => ({
          value: id,
          label: name,
        })),
      );
  }

  @action.bound async fetchDailyView(
    { backgroundUpdate, loadForAllUsers }: ResourcePlanningFetchDataArgs = {
      backgroundUpdate: false,
      loadForAllUsers: false,
    },
  ) {
    await this._fetchData(loadForAllUsers, backgroundUpdate);
  }

  // Reuses unchanged rows, days and items, so a sync re-renders only what changed
  @action.bound mergeData(src: TPlanningDailyViewUserByDay[]): void {
    const rowsByUserId = new Map(this.data.map((row) => [row.user.id, row]));
    const data = src.map((rowSrc) => {
      const row = rowsByUserId.get(rowSrc.user.id);
      if (!row) return new PlanningDailyViewUserByDayModel(rowSrc);

      row.update(rowSrc);
      return row;
    });

    if (!hasSameItems(data, this.data)) this.data = data;
  }

  // Called when the planning page unmounts, so the loaded planning doesn't stay in memory app-wide
  @action.bound releaseData(): void {
    this.data = [];
    this.dataSnapshot = undefined;
    this.active = undefined;
    this.dropIndicators.clear();
    this.hoveredItemIds.clear();
    this.hoveredItemsOrder.clear();
    this.fetcher.clearResponses();
  }

  @action.bound async syncData() {
    await this.fetchDailyView({
      backgroundUpdate: true,
      loadForAllUsers: true,
    });
  }

  @action.bound setDropIndicator(
    containerId: string | undefined,
    top: number | null = null,
  ): void {
    const [currentContainerId] = this.dropIndicators.keys();

    if (
      currentContainerId !== undefined &&
      currentContainerId !== containerId
    ) {
      this.dropIndicators.delete(currentContainerId);
    }
    if (containerId === undefined) return;
    if (this.dropIndicators.get(containerId) === top) return;

    this.dropIndicators.set(containerId, top);
  }

  @action.bound optimisticallyMoveAllocationItem(
    payload: MovedAllocationPayload,
  ) {
    const { from_date, to_date, to_user_id, from_user_id, userItem, newIndex } =
      payload;
    if (this.validateMove(payload).itemNotMoved) return;

    for (const assigneeItem of this.data) {
      const userId = assigneeItem.user.id;
      if (userId !== from_user_id && userId !== to_user_id) continue;

      for (const day of assigneeItem.items) {
        const isFromDay =
          userId === from_user_id && isSameDay(day.date, from_date);
        const isToDay = userId === to_user_id && isSameDay(day.date, to_date);
        if (!isFromDay && !isToDay) continue;

        const items = day.items.filter(({ id }) => id !== userItem.id);
        if (isToDay) items.splice(newIndex, 0, userItem);
        day.items = items;
      }
    }
  }

  @action.bound revertMoveAllocationItem(payload: MovedAllocationPayload) {
    this.optimisticallyMoveAllocationItem({
      ...payload,
      from_date: payload.to_date,
      to_date: payload.from_date,
      from_user_id: payload.to_user_id,
      to_user_id: payload.from_user_id,
      oldIndex: payload.newIndex,
      newIndex: payload.oldIndex,
    });
  }

  validateMove(payload: MovedAllocationPayload) {
    const { to_user_id, from_user_id, from_date, to_date, oldIndex, newIndex } =
      payload;

    const fromDateString = from_date.toDateString();
    const toDateString = to_date.toDateString();
    const movedToSameDay = fromDateString === toDateString;
    const movedToSameUser = from_user_id === to_user_id;
    const movedToSameUserAndDay = movedToSameDay && movedToSameUser;
    const onlyReordered = movedToSameUserAndDay && oldIndex !== newIndex;
    const itemNotMoved = movedToSameUserAndDay && oldIndex === newIndex;

    return {
      onlyReordered,
      itemNotMoved,
      movedToSameDay,
      movedToSameUser,
      movedToSameUserAndDay,
    };
  }

  @action.bound async moveAllocationItem(
    movePayload: MovedAllocationPayload,
    newWorkTypeId?: MoveCapacityAllocationItemInput["new_time_tracking_work_type_id"],
  ) {
    const { id, to_user_id, from_user_id, to_date, userItem } = movePayload;

    const { itemNotMoved, onlyReordered, movedToSameDay, movedToSameUser } =
      this.validateMove(movePayload);

    // If the allocation item is dropped in the same place, do nothing
    if (itemNotMoved) {
      return;
    }

    this.syncingIds.set(userItem.id, true);

    if (!onlyReordered) {
      const [data, error] = await this.moveAllocationMutator.mutate({
        input: {
          id,
          to_user_id,
          from_user_id,
          date: toApiDate(to_date),
          new_time_tracking_work_type_id: newWorkTypeId,
        },
      });

      if (error) {
        this.syncingIds.delete(userItem.id);
        return this.syncData();
      }

      if (data?.moveCapacityAllocationItem) {
        if (!movedToSameUser || !movedToSameDay) {
          this.appStore.UIStore.toast({
            status: "warning",
            title: t`This allocation will be disconnected from the series and will require separate editing.`,
          });
        }
        this.locallyUpdateItemsAfterMove({
          userItems: data.moveCapacityAllocationItem.userItems,
          movePayload,
        });
      }
    }

    this.reorderAllocationItems(movePayload);
  }

  @action.bound async duplicateAllocationItem(
    payload: MovedAllocationPayload,
    newWorkTypeId?: MoveCapacityAllocationItemInput["new_time_tracking_work_type_id"],
  ) {
    const { to_user_id, from_user_id, to_date, userItem, newIndex } = payload;
    const { itemNotMoved, onlyReordered } = this.validateMove(payload);

    if (itemNotMoved || onlyReordered) return;

    const dataCount = this.data.length;
    for (let i = 0; i < dataCount; i++) {
      const item = this.data[i];
      if (!item) continue;
      if (item.user.id !== from_user_id) continue;

      const itemCount = item.items.length;

      for (let l = 0; l < itemCount; l++) {
        const userItem = item.items[l];

        if (
          new Date(userItem.date).toDateString() !==
          payload.from_date.toDateString()
        )
          continue;

        userItem.items.splice(payload.oldIndex, 0, payload.userItem);
      }
    }

    this.syncingIds.set(userItem.id, true);

    const [data, error] = await this.copyAllocationMutator.mutate({
      input: {
        id: userItem.id,
        to_user_id: to_user_id,
        from_user_id: from_user_id,
        date: toApiDate(to_date),
        new_time_tracking_work_type_id: newWorkTypeId,
      },
    });

    if (error) {
      this.syncData();
      this.syncingIds.delete(userItem.id);
      return;
    }

    const matchingUserItem = this.data.find(
      ({ user }) => user.id === to_user_id,
    );

    if (!matchingUserItem) return;
    const resUserItem = data?.copyCapacityAllocationItem;
    if (!resUserItem) return;
    const itemCount = matchingUserItem.items.length;

    for (let i = 0; i < itemCount; i++) {
      const userItem = matchingUserItem.items[i];
      if (new Date(userItem.date).toDateString() !== to_date.toDateString())
        continue;
      userItem.items.splice(
        newIndex,
        1,
        new PlanningDailyViewItemModel(resUserItem, resUserItem.user),
      );
    }

    this.appStore.UIStore.toast({
      status: "warning",
      title: t`This allocation will be disconnected from the series and will require separate editing.`,
    });

    this.syncingIds.clear();
  }

  isSyncing(id: PlanningDailyViewItemModel["id"]): boolean {
    return this.syncingIds.has(id);
  }

  wasHovered(id: PlanningDailyViewItemModel["id"]): boolean {
    return this.hoveredItemIds.has(id);
  }

  @action.bound markHovered(id: PlanningDailyViewItemModel["id"]): void {
    this.hoveredItemsOrder.delete(id);
    this.hoveredItemsOrder.add(id);
    if (!this.hoveredItemIds.has(id)) this.hoveredItemIds.set(id, true);

    if (this.hoveredItemsOrder.size <= MAX_HOVERED_ITEMS) return;

    const [oldestId] = this.hoveredItemsOrder;
    this.hoveredItemsOrder.delete(oldestId);
    this.hoveredItemIds.delete(oldestId);
  }

  public async checkIfUserInTask(
    userId: Scalars["ID"]["output"],
    taskId: Scalars["ID"]["output"],
  ) {
    const [data] = await this.userInTaskFetcher.fetch({
      userId,
      taskId,
    });

    return data?.isUserInTask;
  }

  @action.bound async reorderAllocationItems({
    userItem,
    to_date,
    to_user_id,
  }: MovedAllocationPayload) {
    const ids =
      this.data
        .find((assigneeItem) => assigneeItem.user.id === to_user_id)
        ?.items.find((day) => isSameDay(new Date(day.date), to_date))
        ?.items?.map(({ id }) => id) ?? [];

    const [, error] = await this.reorderAllocationMutator.mutate({
      ids,
    });

    this.syncingIds.delete(userItem.id);

    if (error) {
      this.syncData();
    }
  }

  @action.bound locallyUpdateItemsAfterMove({
    userItems,
    movePayload,
  }: {
    userItems: MoveCapacityAllocationItemMutation["moveCapacityAllocationItem"]["userItems"];
    movePayload: MovedAllocationPayload;
  }): void {
    const { to_date, to_user_id, userItem } = movePayload;

    userItems.forEach((updatedUserItem) => {
      let foundUserItem = this.findUserItem({
        id: updatedUserItem.id,
        userId: updatedUserItem.user.id,
        date: new Date(updatedUserItem.date),
      });

      // If the item was moved, we need to find it in the new place because ID could be changed
      if (
        to_user_id === updatedUserItem.user.id &&
        to_date.toDateString() === new Date(updatedUserItem.date).toDateString()
      ) {
        foundUserItem = this.findMovedUserItem({
          id: userItem.id,
          to_user_id,
          to_date,
        });
      }

      foundUserItem?.applyLocalChanges({
        completed: updatedUserItem.completed,
        user: updatedUserItem.user,
        time_tracking_work_type_id: updatedUserItem.time_tracking_work_type_id,
        id: updatedUserItem.id,
      });
    });
  }

  async checkIfSyncNeeded() {
    // The next check syncs, `lastSyncedAt` stays unchanged
    if (this.active) return;

    const [data] = await this.syncCheckMutation.mutate({
      date: this.lastSyncedAt,
    });

    if (data?.isDateBeforeLastPlanningUpdate) {
      this.syncData();
    }
  }

  snapshotData(): void {
    const days: PlanningDataSnapshot["days"] = new Map();
    const dayItems: PlanningDataSnapshot["dayItems"] = new Map();

    for (const assigneeItem of this.data) {
      days.set(assigneeItem, assigneeItem.items);
      for (const day of assigneeItem.items) {
        dayItems.set(day, day.items.slice());
      }
    }

    this.dataSnapshot = { data: this.data.slice(), days, dayItems };
  }

  clearSnapshot(): void {
    this.dataSnapshot = undefined;
  }

  @action.bound revertChanges(): void {
    const snapshot = this.dataSnapshot;
    this.dataSnapshot = undefined;
    if (!snapshot) return;

    snapshot.days.forEach((days, assigneeItem) => {
      assigneeItem.items = days;
    });
    snapshot.dayItems.forEach((items, day) => {
      day.items = items;
    });
    this.data = snapshot.data;
  }

  /**
   * @param [deleteItemsAfterDeleteDate=true] default = true
   */
  locallyDeleteCapacityAllocation(
    capacityAllocationId: CapacityAllocation["id"],
    deleteDate?: CapacityAllocationItem["date"],
    deleteItemsAfterDeleteDate = true,
  ) {
    if (deleteDate) {
      this.data.forEach((assigneeItem) => {
        assigneeItem.items.forEach((day) => {
          day.items = day.items.filter(({ stats, date }) => {
            return (
              (deleteItemsAfterDeleteDate
                ? isBefore(date, deleteDate)
                : !isSameDay(date, deleteDate)) ||
              stats?.capacity_allocation_id !== capacityAllocationId
            );
          });
        });
      });
    } else {
      this.data.forEach((assigneeItem) => {
        assigneeItem.items.forEach((day) => {
          day.items = day.items.filter(({ stats }) => {
            return stats?.capacity_allocation_id !== capacityAllocationId;
          });
        });
      });
    }
  }

  locallyDeleteAllocationItem(id: CapacityAllocationItem["id"]) {
    this.data.forEach((assigneeItem) => {
      assigneeItem.items.forEach((day) => {
        day.items = day.items.filter((item) => item.id !== id);
      });
    });
  }

  findUserItem(item: {
    id: PlanningDailyViewItemModel["id"];
    userId: User["id"];
    date: Date;
  }): PlanningDailyViewItemModel | undefined {
    return this.data
      .find((assigneeItem) => assigneeItem.user.id === item.userId)
      ?.items.find(
        (day) => new Date(day.date).toDateString() === item.date.toDateString(),
      )
      ?.items.find(({ id }) => id === item.id);
  }

  findMovedUserItem(movedItem: {
    id: PlanningDailyViewItemModel["id"];
    to_user_id: User["id"];
    to_date: Date;
  }): PlanningDailyViewItemModel | undefined {
    return this.data
      .find((assigneeItem) => assigneeItem.user.id === movedItem.to_user_id)
      ?.items.find(
        (day) =>
          new Date(day.date).toDateString() ===
          movedItem.to_date.toDateString(),
      )
      ?.items.find(({ id }) => id === movedItem.id);
  }

  @action.bound
  async declineImportedAllocation(
    id: CapacityAllocation["id"],
    declineForUserId?: User["id"],
  ) {
    this.deletingAllocationItemId.add(id);

    const [, error] = await this.deleteAllocationMutator.mutate({
      input: {
        id,
        ...(declineForUserId ? { decline_for_user_id: declineForUserId } : {}),
      },
    });

    this.deletingAllocationItemId.delete(id);
    if (error) return;

    this.locallyDeleteAllocationItem(id);
    this.syncData();
  }

  @action.bound
  declineImportedAllocationForUser(
    id: CapacityAllocation["id"],
    userId: User["id"],
  ) {
    this.confirmSeparateAllocationModal.onOpen({
      onConfirm: () => this.declineImportedAllocation(id, userId),
    });
  }

  @action.bound
  acceptImportedAllocation(item: PlanningDailyViewItemModel) {
    this.handleEdit(item, "allOccurrences");
  }

  @action.bound
  declineImportedAllocationItem({ stats }: PlanningDailyViewItemModel) {
    if (!stats?.capacity_allocation_id) return;

    this.declineImportedAllocation(stats.capacity_allocation_id);
  }

  @action.bound
  declineImportedAllocationItemForUser({
    stats,
    user,
  }: PlanningDailyViewItemModel) {
    if (!stats?.capacity_allocation_id) return;

    this.declineImportedAllocationForUser(
      stats.capacity_allocation_id,
      user.id,
    );
  }

  @action.bound
  acceptImportedAllocationForUser(item: PlanningDailyViewItemModel) {
    this.confirmSeparateAllocationModal.onOpen({
      onConfirm: () =>
        this.handleEdit(item, "currentUser", undefined, item.user.id),
    });
  }

  @action.bound
  async handleEdit(
    item: PlanningDailyViewItemModel,
    affectOccurrences: AffectAllocationOccurrences,
    handleChangeLoading?: (isLoading: boolean) => void,
    acceptForUserId?: User["id"],
  ) {
    if (handleChangeLoading) {
      handleChangeLoading(true);
    } else {
      this.deletingAllocationItemId.add(item.id);
    }

    const { data } = await client
      .query<
        CapacityAllocationUserItemDetailQuery,
        CapacityAllocationUserItemDetailQueryVariables
      >(CapacityAllocationUserItemDetailDocument, {
        id: item.id,
        today: getTodayStart(),
      })
      .toPromise();

    if (handleChangeLoading) {
      handleChangeLoading(false);
    } else {
      this.deletingAllocationItemId.delete(item.id);
    }

    if (!data?.capacityAllocationItemDetailQuery) {
      this.appStore.UIStore.toast({
        status: "error",
        title: t`Failed to fetch allocation details`,
      });
      return;
    }

    this.appStore.allocationModalStore.modalState.onOpen({
      affectOccurrences,
      acceptForUserId,
      formData: {
        id:
          affectOccurrences === "allOccurrences" ||
          affectOccurrences === "currentUser"
            ? item.stats?.capacity_allocation_id
            : item.stats?.capacity_allocation_item_id,
        item: data?.capacityAllocationItemDetailQuery,
        assignees: uniqBy(
          data.capacityAllocationItemDetailQuery.userItems,
          "user.id",
        ),
        tracked_for_item: item.tracked_for_item,
      },
      onSubmit: () =>
        this.fetchDailyView({
          backgroundUpdate: false,
          loadForAllUsers: true,
        }),
    });
  }
}
